import { styled } from '@porsche-code/kado-styled-system/jsx';
import { Spacer, Spinner } from '@porsche-code/kado-ui';
import {
  PButton,
  PIcon,
  PInlineNotification,
  PSpinner,
} from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ClientError } from 'graphql-request';
import { Trans, useTranslation } from 'react-i18next';

import {
  PpnMasterdataImportStatusQuery,
  usePpnMasterdataImportStatusQuery,
  useStartPpnMasterdataImportMutation,
} from '@/graphql';

export const Route = createFileRoute('/admin/ppn-masterdata-import/status')({
  component: Status,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="admin.ppnMasterdataImport.status" />,
    };
  },
});

function Status() {
  const { t } = useTranslation();

  const { data: ppnMasterdataImportStatus, isPending } =
    usePpnMasterdataImportStatusQuery(undefined, {
      refetchInterval: 5000,
      select: (data) => data.ppnMasterdataImportStatus,
    });

  const queryClient = useQueryClient();
  const {
    mutate: startImport,
    isPending: isStarting,
    error: startImportError,
    reset: resetStartImport,
  } = useStartPpnMasterdataImportMutation({
    onSuccess: (data) => {
      queryClient.setQueryData<PpnMasterdataImportStatusQuery>(
        usePpnMasterdataImportStatusQuery.getKey(),
        {
          ppnMasterdataImportStatus: data.startPpnMasterdataImport,
        },
      );
    },
  });

  return (
    <>
      {isPending ? (
        <Spinner size="xl" />
      ) : (
        <>
          {ppnMasterdataImportStatus && (
            <div>
              <ImportStep
                label={t('admin.ppnMasterdataImport.import')}
                state="success"
                startedAt={ppnMasterdataImportStatus.startedAt}
                error={ppnMasterdataImportStatus?.error}
              />
              <ImportStep
                label={t('admin.ppnMasterdataImport.importGroups')}
                state={
                  ppnMasterdataImportStatus.groupsFinishedAt
                    ? 'success'
                    : ppnMasterdataImportStatus.error
                      ? 'error'
                      : 'loading'
                }
                finishedAt={ppnMasterdataImportStatus.groupsFinishedAt}
                error={ppnMasterdataImportStatus?.error}
              />
              <ImportStep
                label={t('admin.ppnMasterdataImport.importOrganizations')}
                state={
                  ppnMasterdataImportStatus.organizationsFinishedAt
                    ? 'success'
                    : ppnMasterdataImportStatus.groupsFinishedAt
                      ? ppnMasterdataImportStatus.error
                        ? 'error'
                        : 'loading'
                      : 'waiting'
                }
                finishedAt={ppnMasterdataImportStatus.organizationsFinishedAt}
                error={ppnMasterdataImportStatus?.error}
              />
              <ImportStep
                label={t('admin.ppnMasterdataImport.importPersons')}
                state={
                  ppnMasterdataImportStatus.personsFinishedAt
                    ? 'success'
                    : ppnMasterdataImportStatus.organizationsFinishedAt
                      ? ppnMasterdataImportStatus.error
                        ? 'error'
                        : 'loading'
                      : 'waiting'
                }
                finishedAt={ppnMasterdataImportStatus.personsFinishedAt}
                error={ppnMasterdataImportStatus?.error}
              />
              {(ppnMasterdataImportStatus.finishedAt ||
                ppnMasterdataImportStatus.personsFinishedAt) && (
                <ImportStep
                  label={t('admin.ppnMasterdataImport.import')}
                  state={
                    ppnMasterdataImportStatus.finishedAt ? 'success' : 'error'
                  }
                  finishedAt={ppnMasterdataImportStatus.finishedAt}
                  error={ppnMasterdataImportStatus?.error}
                />
              )}
            </div>
          )}

          <Spacer mt="md" />

          {ppnMasterdataImportStatus?.isRunning &&
            ppnMasterdataImportStatus?.error && (
              <>
                <PInlineNotification
                  heading={t('admin.ppnMasterdataImport.error')}
                  description={t(
                    'admin.ppnMasterdataImport.importStuckRunning',
                  )}
                  state="error"
                  dismissButton={false}
                />

                <Spacer mt="md" />
              </>
            )}

          <PButton
            onClick={() => startImport({})}
            type="button"
            loading={isStarting}
            disabled={ppnMasterdataImportStatus?.isRunning || isPending}
          >
            {t('admin.ppnMasterdataImport.triggerImportFromPPN')}
          </PButton>

          {startImportError instanceof ClientError && (
            <>
              <Spacer mt="md" />

              <PInlineNotification
                heading={t('admin.ppnMasterdataImport.error')}
                description={
                  startImportError.response.errors?.[0].message ??
                  startImportError.message
                }
                state="error"
                onDismiss={resetStartImport}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

const ImportStep = ({
  label,
  state,
  startedAt,
  finishedAt,
  error,
}: {
  label: string;
  state: 'success' | 'loading' | 'waiting' | 'error';
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dateFormat = new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <>
      {state === 'error' && error ? (
        <PInlineNotification
          heading={label}
          description={error}
          state="error"
          dismissButton={false}
        />
      ) : (
        <ImportStepWrapper>
          {state === 'success' && (
            <PIcon name="success-filled" size="md" color="success" />
          )}
          {state === 'loading' && <PSpinner />}
          {state === 'waiting' && (
            <PIcon name="clock" size="md" color="contrast-low" />
          )}
          {state === 'error' && (
            <PIcon name="warning-filled" size="md" color="error" />
          )}
          {label}
          <span>
            {startedAt &&
              t('admin.ppnMasterdataImport.startedAt', {
                date: dateFormat.format(new Date(startedAt)),
              })}
            {finishedAt &&
              t('admin.ppnMasterdataImport.finishedAt', {
                date: dateFormat.format(new Date(finishedAt)),
              })}
          </span>
        </ImportStepWrapper>
      )}
    </>
  );
};

const ImportStepWrapper = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'xs',
  },
});
